import useAppMainConfig from '@modules/ui/shared/hooks/useAppMainConfig';
import React, { FunctionComponent } from 'react';
import { Grid, makeStyles, useMediaQuery } from '@rbx/ui';
import { useAppConfig } from './AppConfigProvider';
import {
  mainID,
  rightNavMinBreakpoint,
  rightNavPaddingX,
  rightNavWidth,
} from '../constants/layoutConstants';
import { LayoutType } from './AppLayout';

type TAppPageContentProps = {
  rightNav?: JSX.Element;
  layoutType?: LayoutType;
};

const MaxSidebarWidth = rightNavWidth + rightNavPaddingX;

const useStyles = makeStyles<{
  maxWidth: number;
  enableRightNav: boolean;
  layoutType?: LayoutType;
}>()((_, { maxWidth, enableRightNav, layoutType }) => ({
  appPageContainer: {
    flexDirection: 'row',
    width: '100%',
    overflowWrap: 'break-word',
    justifyContent: 'center',
  },
  contentContainer: {
    display: 'flex',
    width: '100%',
    maxWidth: `min(100%, calc(${maxWidth}px + 300px))`,
  },
  content: {
    // NOTE (ahua, 2025-11-12): OpenCloudV2 has code samples which occupy 50% of the width,
    // so make content width 100% on OpenCloudV2 docs, since remaining content will be narrow enough
    width: `calc(100% - ${enableRightNav && layoutType !== LayoutType.OpenCloudV2 ? MaxSidebarWidth : 0}px)`,
  },
  sidebar: {
    width: MaxSidebarWidth,
  },
}));

const AppPageContent: FunctionComponent<React.PropsWithChildren<TAppPageContentProps>> = ({
  rightNav,
  children,
  layoutType,
}) => {
  const {
    mainMaxWidth,
    rightNav: { isEmpty: rightNavIsEmpty },
  } = useAppMainConfig();
  const { hideInPageNavigation, isLandingPage } = useAppConfig();

  // NOTE (tchu, 2025-10-22): per design (John Leonard), the content should not
  // take the full width of the page when the right nav is empty,
  // only when in the YAML frontmatter we specify hideInPageNavigation: true
  const enableRightNav =
    useMediaQuery((theme) => theme.breakpoints.up(rightNavMinBreakpoint)) &&
    !hideInPageNavigation &&
    !isLandingPage;

  const {
    classes: { appPageContainer, contentContainer, content, sidebar },
  } = useStyles({ maxWidth: mainMaxWidth, enableRightNav, layoutType });

  return (
    <Grid container classes={{ root: appPageContainer }}>
      <Grid classes={{ root: contentContainer }}>
        <Grid classes={{ root: content }} component='main' id={mainID}>
          {children}
        </Grid>
        {enableRightNav && !rightNavIsEmpty && (
          <Grid classes={{ root: sidebar }}>
            <Grid position='fixed'>{rightNav}</Grid>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default AppPageContent;
